/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import AuthMainLayout from 'components/ChoreoSystem/Authenticate/AuthMainLayout/AuthMainLayout';
import Link from 'components/ChoreoSystem/Link/Link';
import { enableAsgardeoMaintenanceFlag, supportCommunity } from 'config';
import { isChrome, isFirefox, setCookie } from 'utils/browser';
import { getPendingCopilotPrompt } from 'utils/copilotPrompt';
import { COPILOT_NEW_PATH } from 'utils/route';
import PreLoader from 'pages/preloader/PreLoader';
import SignupCard from 'modules/signup/SignupCard';
import useSignUpFlow from 'hooks/authFlows/useSignUpFlow';
import UnderMaintenanceBanner from 'components/UnderMaintenanceBanner/UnderMaintenanceBanner';

export const IS_FROM_CONTEST_PAGE = 'isFromContestPage';

interface LocationState {
  returnToUrl: string;
  returnToOrg: string;
  displayOrgName: string;
}

function Signup() {
  const { state, search } = useLocation<LocationState>();
  const returnToUrl = state?.returnToUrl || '/';
  const returnToOrg = state?.returnToOrg || '';
  const searchParamsFromReturnToUrl = state?.returnToUrl?.replace(/^\/?\?/, '');
  const isSupportedEngine = isChrome() || isFirefox();
  const [queryParams, setQueryParams] = useState(
    new URLSearchParams(searchParamsFromReturnToUrl || search)
  );

  // A Copilot prompt typed on the login page is waiting to be handed over, so
  // send the user to the create integration page of the Default project rather
  // than the usual landing page. Only ever overrides the default destination —
  // an explicit `returnToUrl` (an invitation, a deep link) always wins.
  //
  // Read once in a lazy initialiser rather than on every render: the getter
  // sweeps expired and corrupt entries as it reads, so it is not a pure read
  // and has no business running during render.
  const [hasPendingPrompt] = useState(() => !!getPendingCopilotPrompt());
  const effectiveReturnToUrl =
    returnToUrl === '/' && hasPendingPrompt ? COPILOT_NEW_PATH : returnToUrl;

  const {
    isLoading,
    handleEmailSignup,
    handleGithubSignup,
    handleGoogleSignup,
    handleMicrosoftSignup,
  } = useSignUpFlow({
    returnToUrl: effectiveReturnToUrl,
    returnToOrg,
    queryParams,
  });
  const [isMobile, setIsMobile] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const isFromContestPage = params.get('isFromContestPage');
  if (isFromContestPage) {
    setCookie(IS_FROM_CONTEST_PAGE, isFromContestPage);
  }

  const registrationUnavailableBannerDescription = () => (
    <FormattedMessage
      id="src.pages.RegisterOrganization.RegistrationUnavailableBanner.description"
      defaultMessage="
        Thank you for your interest in WSO2 Integration Platform. We're currently performing some essential maintenance
        to improve our platform.{newLine}New registrations are temporarily unavailable during this period.
        We apologize for the inconvenience and ask that you please try again shortly.{newLine} {supportLink}"
      values={{
        newLine: (
          <div>
            <br />
          </div>
        ),
        supportLink: (
          <Link
            href={supportCommunity}
            color="primary"
            testId="visit-support-link"
          >
            <FormattedMessage
              id="src.components.UserExceedBanner.description.link"
              defaultMessage="Contact Support"
            />
          </Link>
        ),
      }}
    />
  );

  useEffect(() => {
    const searchParams = state?.returnToUrl?.replace(/^\/?\?/, '');
    const qParams = new URLSearchParams(searchParams || search);
    setQueryParams(qParams);
  }, [state, search]);

  const handleResize = () => {
    if (window.innerWidth < 1000) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    // Measure once on mount as well, or the layout stays in its desktop form
    // on a phone until the first resize.
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isLoading) {
    return <PreLoader />;
  }

  if (enableAsgardeoMaintenanceFlag) {
    return (
      <UnderMaintenanceBanner
        isOpen
        title={
          <FormattedMessage
            defaultMessage="Registration Temporarily Paused"
            id="pages.signup.registrationTemporarilyPaused.title"
          />
        }
        description={registrationUnavailableBannerDescription()}
      />
    );
  }

  return (
    <AuthMainLayout isMobile={isMobile} isSupportedEngine={isSupportedEngine}>
      <SignupCard
        onEmailClick={handleEmailSignup}
        onGithubClick={handleGithubSignup}
        onGoogleClick={handleGoogleSignup}
        onMicrosoftClick={handleMicrosoftSignup}
        isMobile={isMobile}
      />
    </AuthMainLayout>
  );
}

export default Signup;
